import { inject, Injectable, signal } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import {
	doc,
	docData,
	DocumentReference,
	Firestore,
	getDoc,
	updateDoc,
} from '@angular/fire/firestore';
import { PersonalData, Role, UserModel } from '../interfaces/user.model';
import { LogService } from './log.service';
import { LogType } from '../interfaces/log.model';
import { Observable, Subject, Subscription } from 'rxjs';
import { User } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { isPersonalDataInvalid } from '@guards/onboardingGuard.guard';
import { DialogService } from 'primeng/dynamicdialog';
import { GlobalScoreComponent } from '@shared/components/global-score/global-score.component';

@Injectable({
	providedIn: 'root',
})
export class UserService {
	// Services
	private readonly auth = inject(Auth);
	private readonly router = inject(Router);
	private readonly http = inject(HttpClient);
	private readonly firestore = inject(Firestore);
	private readonly logService = inject(LogService);
	private readonly dialogService = inject(DialogService);
	private readonly _closeAll = new Subject<void>();
	public readonly closeAllDialogs$ = this._closeAll.asObservable();

	// Public properties
	public user = signal<UserModel | null>(null);

	// Private properties
	private userSubscription: Subscription | null = null;

	// Constructor
	constructor() {
		authState(this.auth).subscribe(async (firebaseUser) => {
			if (firebaseUser) {
				await this.evalUserLogin(firebaseUser);
			} else {
				this.user.set(null);
			}
		});
	}

	public closeAllDialogs(): void {
		this._closeAll.next();
	}

	public hasRole(role: Role): boolean {
		if (!this.user()) {
			return false;
		}
		return this.user()!.role === role;
	}

	public async evalUserLogin(firebaseUser: User): Promise<void> {
		const userDoc = await getDoc(
			doc(
				this.firestore,
				`companies/${environment.companyId}/users/${firebaseUser.uid}`
			) as DocumentReference<UserModel>
		);

		const userModel = UserModel.fromFirebaseUser(
			firebaseUser,
			userDoc.data()
		);

		if (userDoc.exists()) {
			console.log('User exists');
			await this.updateUser(userModel);

			this.logService.saveLog(
				this.user()!,
				LogType.Login,
				'User logged in successfully',
				userModel.lastLoginAt
			);

			if (isPersonalDataInvalid(userDoc.data())) {
				this.router.navigate(['/onboarding']);
			}
		} else {
			console.log('User does not exist, new user', {
				creatingNewUser: this.user(),
			});
		}

		this.user.set(userModel);
		this.listenUser();
	}

	private async updateUser(user: UserModel): Promise<void> {
		await updateDoc(
			doc(
				this.firestore,
				`companies/${environment.companyId}/users`,
				user.id
			),
			{
				lastLoginAt: user.lastLoginAt,
			}
		);
	}

	public updatePersonalData(personalData: PersonalData): Promise<void> {
		return updateDoc(
			doc(
				this.firestore,
				`companies/${environment.companyId}/users`,
				this.user()!.id
			),
			{
				personalData,
			}
		);
	}

	public updateProfilePic(profilePic: string): Promise<void> {
		return updateDoc(
			doc(
				this.firestore,
				`companies/${environment.companyId}/users`,
				this.user()!.id
			),
			{
				personalData: {
					...this.user()!.personalData,
					profilePic,
				},
			}
		);
	}

	public createNewUser(personalData: PersonalData): Observable<unknown> {
		return this.http.post(`${environment.api_url}/users/create`, {
			companyId: environment.companyId,
			user: {
				...this.user()!,
				personalData,
			},
		});
	}

	public validateDni(dni: string): Observable<{
		status: number;
		message: string;
		email: string;
	}> {
		return this.http.post<{
			status: number;
			message: string;
			email: string;
		}>(`${environment.api_url}/users/validate-dni`, {
			companyId: environment.companyId,
			dni,
		});
	}

	public validatePhone(phone: string): Observable<{
		status: number;
		message: string;
		email: string;
	}> {
		return this.http.post<{
			status: number;
			message: string;
			email: string;
		}>(`${environment.api_url}/users/validate-phone`, {
			companyId: environment.companyId,
			phone,
		});
	}

	public listenUser(): void {
		if (this.userSubscription) {
			this.userSubscription.unsubscribe();
		}

		this.userSubscription = docData(
			doc(
				this.firestore,
				`companies/${environment.companyId}/users/${this.user()!.id}`
			) as DocumentReference<UserModel>
		).subscribe((user) => {
			console.log({ userFromFirestore: user });
			console.log({
				oldScore: this.user()!.score,
				newScore: user?.score,
			});
			if (!user) {
				this.router.navigate(['/onboarding']);
			}
			if (user && user.id) {
				const oldScore = this.user()!.score ?? 0;
				const newScore = user.score ?? 0;
				this.showScoreUpdate(oldScore, newScore);
				this.user.set(user);
			}
		});
	}

	public getUserQRData(): string {
		return JSON.stringify({
			userId: this.user()!.id,
			type: 'user',
		});
	}

	private showScoreUpdate(oldScore: number, newScore: number): void {
		if (!newScore) {
			return;
		}

		if (oldScore !== newScore && newScore > oldScore) {
			console.log({
				dialogComponentRefMap: this.dialogService.dialogComponentRefMap,
			});
			this.closeAllDialogs();
			this.dialogService.dialogComponentRefMap.forEach((dialog) => {
				console.log(dialog);
				dialog.destroy();
			});
			this.dialogService.open(GlobalScoreComponent, {
				data: {
					oldScore,
					newScore,
					name: this.user()!.personalData.firstName,
					profilePic: this.user()!.personalData.profilePic,
				},
				header: 'Capynotificación 🥳',
				closable: true,
				width: '400px',
				modal: true,
				baseZIndex: 1000,
				draggable: false,
				focusOnShow: false,
			});
		}
	}
}
